import React, { useState, useRef, useEffect } from "react";
import { t } from "i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faCommentQuestion,
  faEllipsisVertical,
} from "@fortawesome/pro-regular-svg-icons";
import { faUser, faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import { useAuth } from "react-oidc-context";
import dateHelper from "../../../helpers/dateHelper";
import YourJourneyCatchUpDto from "../../../types/dtos/catch-ups/YourJourneyCatchUpDto";
import AppRoutes from "../../../routes/AppRoutes";
import SimpleAccordion from "../../common/SimpleAccordion";
import Avatar from "../../common/Avatar";
import listingHelper from "../../../helpers/listingHelper";
import { AlertPopup } from "../../common";
import catchUpApi from "../../../api/dashboard/catchUpApi";

interface JourneyCatchUpItemProps {
  currentUserId: number;
  catchUp: YourJourneyCatchUpDto;
  isCompletedTab: boolean;
  onCancelCatchUp?: (answerSetGuidId: string) => void;
}

function JourneyCatchUpItem({
  currentUserId,
  catchUp,
  isCompletedTab,
  onCancelCatchUp,
}: JourneyCatchUpItemProps) {
  const auth = useAuth();
  const apiCatchUp = new catchUpApi(auth.user?.access_token);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [hasButtonBeenClicked, setHasButtonBeenClicked] =
    useState<boolean>(false);
  const [showCancelConfirmation, setShowCancelConfirmation] =
    useState<boolean>(false);
  const [isCanceling, setIsCanceling] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const redirectUrl = AppRoutes.collaborativeDocument.generateRoute(
    catchUp.answerSetGuidId,
    false,
    null
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle button click
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion from toggling
    setHasButtonBeenClicked(true);
    // Navigation will happen via the Link component
  };

  // Handle three-dot menu click
  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion from toggling
    setShowDropdown(!showDropdown);
  };

  // Handle cancel button click
  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent accordion from toggling
    setShowCancelConfirmation(true);
  };

  // Handle cancel confirmation
  const handleConfirmCancel = () => {
    setIsCanceling(true);

    const successCallback = (data: boolean | null) => {
      setIsCanceling(false);
      setShowCancelConfirmation(false);

      if (data && onCancelCatchUp) {
        onCancelCatchUp(catchUp.answerSetGuidId);
      }
    };

    const errorCallback = (error: any) => {
      setIsCanceling(false);
      console.error("Error canceling catch up", error);
      // You might want to show an error message here
    };

    apiCatchUp.cancelCatchUp(
      catchUp.answerSetGuidId,
      successCallback,
      errorCallback
    );
  };

  // Handle cancel cancellation
  const handleCancelCancellation = () => {
    setShowCancelConfirmation(false);
  };

  // Content for the accordion trigger/header
  const renderAccordionTrigger = () => {
    // Format the dates
    const formattedDateScheduled = catchUp.dateScheduled
      ? dateHelper
          .convertUtcDateToLocal(catchUp.dateScheduled)
          .toLocaleDateString() +
        " " +
        dateHelper
          .convertUtcDateToLocal(catchUp.dateScheduled)
          .toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
      : "Not available";

    return (
      <div className="flex justify-between items-center w-full py-3 px-4 bg-gray-50">
        <div className="flex flex-col items-start">
          <p className="font-medium text-base text-gray-700">{catchUp.title}</p>
          <div className="flex flex-col text-sm sm:flex-row sm:space-x-4 mt-1">
            <div className="flex items-center text-gray-500">
              <FontAwesomeIcon
                icon={faUser}
                className="mr-1 text-gray-500"
                size="xs"
              />
              <span>{catchUp.otherContributorFullName}</span>
            </div>
            <div className="flex items-center text-gray-500">
              <FontAwesomeIcon
                icon={faCalendar}
                className="mr-1 text-gray-500"
                size="xs"
              />
              <span>{formattedDateScheduled}</span>
            </div>
            <div className="flex items-center text-gray-500">
              <FontAwesomeIcon
                icon={faCommentQuestion}
                className="mr-1 text-gray-500"
                size="xs"
              />
              <span>
                {catchUp.subjectEmployeeId === currentUserId
                  ? "About you"
                  : "About " + catchUp.otherContributorFirstName}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`text-xs px-2 py-0.5 rounded-full ${listingHelper.getJourneyStatusColor(
              catchUp
            )}`}
          >
            {listingHelper.getJourneyStatusText(catchUp)}
          </span>

          {/* Primary action button */}
          <Link
            to={redirectUrl}
            onClick={handleButtonClick}
            className="btn-primary text-sm py-1 px-4 w-24 text-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {hasButtonBeenClicked ? (
              <span className="flex items-center justify-center">
                <span>{t("Common.PleaseWait")}</span>
              </span>
            ) : isCompletedTab ? (
              t("Common.View")
            ) : (
              t("Common.Update")
            )}
          </Link>

          {/* Cancel button - only show for active catch-ups */}
          {!isCompletedTab && (
            <button
              onClick={handleCancelClick}
              className="btn-secondary text-sm py-1 px-3 text-red-500 hover:text-red-600 border-red-400 hover:border-red-500"
              title="Cancel catch up"
            >
              <FontAwesomeIcon icon={faTrashAlt} size="sm" />
            </button>
          )}
        </div>
      </div>
    );
  };

  const getPhaseText = (): string => {
    if (catchUp.hasNotBeenStarted) {
      // Journey hasn't been started yet
      return t("Pages.Admin.Common.NotStarted");
    }

    if (
      catchUp.approvalStatusEnum !== null &&
      catchUp.approvalStatusEnum === "PARTIALLY-SAVED-JOURNEY"
    ) {
      // Journey is partially saved
      return t("Pages.Admin.Common.InProgress");
    }

    if (catchUp.isCompleted) {
      // Journey is completed
      return t("Pages.Admin.Common.Completed");
    }

    if (!catchUp.isCompleted) {
      // Journey is hidden to employee approval flow
      if (
        catchUp.approvalStatusEnum &&
        catchUp.approvalStatusEnum === "INITIAL-SAVE-FORM-INCOMPLETE"
      ) {
        return t("Pages.Admin.Common.NotStarted");
      }
      return t("Pages.Admin.Common.InProgress");
    }

    return "Unknown";
  };

  // Content for the expanded accordion section
  const renderExpandedContent = () => {
    // Determine the phase of the journey (similar to JourneyFormStatusInfo)
    let phaseText = getPhaseText();

    return (
      <div className="pt-3 pb-3 px-4">
        {/* Basic info displayed as badges */}
        <div className="text-sm mb-3 flex flex-wrap gap-2">
          <span className="text-xs text-gray-600 bg-gray-100 rounded px-2 py-1">
            Catch Up
          </span>
          <span className="text-xs text-gray-600 bg-gray-100 rounded px-2 py-1">
            {catchUp.subjectEmployeeId === currentUserId
              ? "About you"
              : "About " + catchUp.otherContributorFirstName}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 border-t pt-3 bg-gray-50 p-4 rounded-md pl-10">
          {/* Left column with people and dates */}
          <div className="flex flex-col space-y-3">
            <div className="flex items-start">
              <div className="w-32 text-gray-500 text-sm">With</div>
              <div className="flex items-center">
                <Avatar
                  imageUrl={catchUp.otherContributorProfilePic}
                  userFullName={catchUp.otherContributorFullName}
                  userInitials={catchUp.otherContributorInitials || ""}
                  size={8}
                />
                <span className="text-sm font-medium ml-2">
                  {catchUp.otherContributorFullName}
                </span>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-32 text-gray-500 text-sm">Scheduled</div>
              <div className="flex items-center">
                <span className="text-sm font-medium">
                  {catchUp.dateScheduled
                    ? dateHelper
                        .convertUtcDateToLocal(catchUp.dateScheduled)
                        .toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        }) +
                      " " +
                      dateHelper
                        .convertUtcDateToLocal(catchUp.dateScheduled)
                        .toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                    : "Not available"}
                </span>
              </div>
            </div>

            {catchUp.dateSent && (
              <div className="flex items-start">
                <div className="w-32 text-gray-500 text-sm">Sent</div>
                <div className="flex items-center">
                  <span className="text-sm font-medium">
                    {dateHelper
                      .convertUtcDateToLocal(catchUp.dateSent)
                      .toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                  </span>
                </div>
              </div>
            )}

            {catchUp.dateLastUpdated && (
              <div className="flex items-start">
                <div className="w-32 text-gray-500 text-sm">Last Updated</div>
                <div className="flex items-center">
                  <span className="text-sm font-medium">
                    {dateHelper
                      .convertUtcDateToLocal(catchUp.dateLastUpdated)
                      .toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Right column with status/phase */}
          <div className="flex flex-col space-y-3">
            <div className="flex items-start">
              <div className="w-32 text-gray-500 text-sm">Status</div>
              <div className={`text-sm font-medium`}>{phaseText}</div>
            </div>

            {catchUp.approvalStatusTranslationKeyIdentifier && (
              <div className="flex items-start">
                <div className="w-32 text-gray-500 text-sm">Phase</div>
                <div className="text-sm font-medium text-gray-700">
                  {t(catchUp.approvalStatusTranslationKeyIdentifier)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <SimpleAccordion
        title={renderAccordionTrigger()}
        className="mb-3 border-[0.5px] border-gray-200 rounded-md"
      >
        {renderExpandedContent()}
      </SimpleAccordion>

      {/* Cancel confirmation dialog */}
      <AlertPopup
        isOpen={showCancelConfirmation}
        onOpenChange={setShowCancelConfirmation}
        onPrimaryButtonClick={handleConfirmCancel}
        onSecondaryButtonClick={handleCancelCancellation}
        primaryButtonText={isCanceling ? t("Common.PleaseWait") : "Yes, cancel"}
        secondaryButtonText="No, keep it"
        title="Cancel Catch Up"
        bodyText={`Are you sure you want to cancel the catch up "${catchUp.title}"? Once cancelled, you will not be able to get this back.`}
      />
    </>
  );
}

export default JourneyCatchUpItem;
